import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextAreaField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { useCollection } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime, formatNumber } from "@/lib/format";
import {
  ALARM_KEY,
  alarmsIn,
  deletedTotals,
  reasonIsSufficient,
  runDurationSeconds,
  type RetentionStatus,
} from "./retention";
import type { Institution } from "@/types";

/**
 * Retention.
 *
 * The banner is the page. Data past its window that is still here is not a
 * metric to sit in a table — it is a promise we made to an institution and to
 * the people in its classes, unkept, and it names whose data it is because that
 * is the first question anybody asks.
 */
export function RetentionPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const status = useCallable<RetentionStatus>(CALLABLES.getRetentionStatus);
  const { rows: institutions } = useCollection<Institution>("institutions");

  const [institutionId, setInstitutionId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const alarms = useMemo(() => alarmsIn(status.data), [status.data]);
  const totals = useMemo(() => deletedTotals(status.data?.lastRun ?? null), [status.data]);
  const duration = runDurationSeconds(status.data?.lastRun ?? null);

  return (
    <SectionShell titleKey="retention.title">
      {status.loading ? (
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      ) : status.failed || !status.data ? (
        <p className="text-sm text-accent">{t("error.network.unavailable")}</p>
      ) : (
        <>
          {alarms.length > 0 ? (
            <div className="rounded-md border border-accent bg-accent-soft px-5 py-4" role="alert">
              <h2 className="font-display text-lg text-ink">{t("retention.breach")}</h2>
              <ul className="mt-2 flex flex-col gap-1 text-sm text-ink">
                {alarms.map((alarm) => (
                  <li key={alarm}>
                    {t(ALARM_KEY[alarm], {
                      count: status.data!.overdueTotal,
                      hours: Math.round(status.data!.missed.hoursSince ?? 0),
                      failures: status.data!.lastRun?.failures.length ?? 0,
                    })}
                  </li>
                ))}
              </ul>

              {status.data.overdue.length > 0 ? (
                <ul className="mt-4 flex flex-col gap-1 text-sm">
                  {status.data.overdue.map((row) => (
                    <li key={row.institutionId}>
                      <Link
                        to={`/institutions/${row.institutionId}`}
                        className="text-ink underline underline-offset-2"
                      >
                        {row.name}
                      </Link>
                      <span className="ml-2 text-ink-soft">
                        {t("retention.overdueLine", {
                          count: row.overdue,
                          days: row.operationalDays,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : (
            <div className="rounded-md border border-cue-green bg-cue-green-soft px-5 py-4">
              <p className="font-display text-lg text-ink">{t("retention.clear")}</p>
              <p className="mt-1 text-sm text-ink-soft">{t("retention.clearExplain")}</p>
            </div>
          )}

          <section className="mt-10">
            <h2 className="font-display text-lg text-ink">{t("retention.lastRun")}</h2>

            {status.data.lastRun ? (
              <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    key: "retention.ranAt",
                    value: formatDateTime(locale, status.data.lastRun.startedAt),
                  },
                  {
                    key: "retention.duration",
                    value:
                      duration === undefined
                        ? t("retention.neverFinished")
                        : t("retention.seconds", { seconds: duration }),
                  },
                  {
                    key: "retention.sessionsDeleted",
                    value: formatNumber(locale, totals.sessions),
                  },
                  { key: "retention.eventsDeleted", value: formatNumber(locale, totals.events) },
                ].map((stat) => (
                  <div
                    key={stat.key}
                    className="rounded-md border border-paper-deep bg-white px-5 py-4"
                  >
                    <dt className="text-sm text-ink-muted">{t(stat.key)}</dt>
                    <dd className="mt-1 font-display text-xl text-ink">{stat.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="mt-3 text-sm text-accent">{t("retention.neverRun")}</p>
            )}
          </section>

          <section className="mt-12 max-w-xl">
            <h2 className="font-display text-lg text-ink">{t("retention.manual")}</h2>
            <p className="mt-1 text-sm text-ink-soft">{t("retention.manualExplain")}</p>

            <div className="mt-4 flex flex-col gap-4">
              <SelectField
                labelKey="retention.institution"
                value={institutionId}
                onChange={(event) => setInstitutionId(event.target.value)}
                options={[
                  { value: "", labelKey: "common.none" },
                  ...institutions.map((institution) => ({
                    value: institution.id,
                    labelKey: institution.name,
                  })),
                ]}
              />

              <TextAreaField
                labelKey="audit.reason"
                hintKey="audit.reasonHint"
                value={reason}
                maxLength={400}
                onChange={(event) => setReason(event.target.value)}
              />

              <div>
                <Button
                  busy={busy}
                  disabled={institutionId === "" || !reasonIsSufficient(reason) || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const report = await call<
                        { institutionId: string; reason: string },
                        { perInstitution: Array<{ sessionsDeleted: number }> }
                      >(CALLABLES.runRetentionForInstitution, {
                        institutionId,
                        reason: reason.trim(),
                      });

                      success("retention.ran", {
                        count: report.perInstitution[0]?.sessionsDeleted ?? 0,
                      });
                      setReason("");
                      status.reload();
                    } catch (error) {
                      showError(error);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t("retention.run")}
                </Button>
              </div>
            </div>
          </section>
        </>
      )}
    </SectionShell>
  );
}
